import { useMemo } from "react";
import styles from "./Graph.module.scss";
import { generateEvenlySpaced } from "Explore/Utils";
import useFilterAndPagination from "Hooks/useFilterAndPagination";
import { ParamsFilterKey } from "Config/filterSearchParams";
import {
  CameraOutlined,
  DragOutlined,
  InfoCircleFilled,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { ImageCheck } from "..";
import {
  AlternativeImage,
  ImageCheckType,
  ImageType,
} from "Explore/Config/ImageType";
import { numberRegex } from "Explore/Utils/numberRegex";
import { Button, Select, MenuItem, FormControl } from "@mui/material";
import classNames from "classnames";
import { IconPack } from "Explore/Config/Icons";
import useAuth from "Hooks/useAuth";
import { useLocation, useNavigate } from "react-router-dom";

const statistics = [
  { label: "citations", key: "total_citation" },
  { label: "citations", key: "citation_count" },
  { label: "faculty members", key: "total_faculties" },
  { label: "h-index", key: "total_h_index" },
  { label: "publications", key: "total_publication" },
  { label: "publications", key: "publication_count" },
  { label: "average academic age", key: "average_seniority" },
];

const InformationBoard = ({
  filter: filterProps,
  selectedElement,
  graphType,
  maxLines,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { filter: _filter, updateSearchParams } = useFilterAndPagination();
  const filter = filterProps || _filter;
  const nodeOptions = useMemo(
    () =>
      graphType == "faculty" ? [10, 20, 30, 70, 100] : [8, 10, 20, 30, 50],
    [graphType]
  );
  const allowChooseEdge = useMemo(() => !!maxLines, [maxLines]);
  const edgeOptions = useMemo(() => {
    let optionValue =
      graphType == "faculty" ? [40, 70, 90, 130, 150] : [10, 25, 50, 75, 90];

    if (maxLines) {
      optionValue = generateEvenlySpaced(maxLines);
    }
    return optionValue;
  }, [maxLines, graphType]);

  const imageCheckType = useMemo(
    () =>
      graphType == "faculty"
        ? ImageCheckType.FACULTY_AVATAR
        : graphType == "unit"
          ? ImageCheckType.CUSTOM
          : "",
    [graphType]
  );
  const getNodeAltLogo = (node) => {
    if (graphType == "unit") {
      if (node?.institution_id) {
        return `https://media.academic.online/institutions_images/${node?.institution_id}/${ImageType.SEAL}.png`;
      } else {
        return AlternativeImage[ImageCheckType.INSTITUTION_LOGO];
      }
    } else {
      return AlternativeImage[ImageCheckType.FACULTY_AVATAR];
    }
  };

  const handleLogin = () => {
    const currentPath = location.pathname + location.search;
    navigate(`/login`, { state: { previousPage: currentPath } });
  };

  return (
    <>
      {!selectedElement && (
        <div className={styles["information-board"]}>
          <p className={styles["information-board--title"]}>Graph settings</p>
          <div className={styles["information-board__content"]}>
            {!!user ? (
              <div className={styles["information-board--settings"]}>
                <div className={styles["information-board--settings--item"]}>
                  <p>Node count</p>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={filter[ParamsFilterKey.TOP_K] || nodeOptions[0]}
                      onChange={(e) => {
                        updateSearchParams({
                          [ParamsFilterKey.TOP_K]: +e.target.value,
                        });
                      }}
                      sx={{
                        borderRadius: "10px",
                        backgroundColor: "#f8f9fc",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(102, 126, 234, 0.2)",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(102, 126, 234, 0.5)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#667eea",
                        },
                      }}
                    >
                      {nodeOptions.map((value) => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                {allowChooseEdge && (
                  <div className={styles["information-board--settings--item"]}>
                    <p>Edge count</p>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={
                          edgeOptions.includes(
                            filter[ParamsFilterKey.TOP_K_EDGES]
                          )
                            ? filter[ParamsFilterKey.TOP_K_EDGES]
                            : ""
                        }
                        onChange={(e) =>
                          updateSearchParams({
                            [ParamsFilterKey.TOP_K_EDGES]: +e.target.value,
                          })
                        }
                        displayEmpty
                        sx={{
                          borderRadius: "10px",
                          backgroundColor: "#f8f9fc",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(102, 126, 234, 0.2)",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(102, 126, 234, 0.5)",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#667eea",
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          --
                        </MenuItem>
                        {edgeOptions.map((value) => (
                          <MenuItem key={value} value={value}>
                            {value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles["information-board--settings--item"]}>
                <p>
                  <span
                    style={{ textDecoration: "underline", cursor: "pointer" }}
                    onClick={() => handleLogin()}
                  >
                    Log in
                  </span>{" "}
                  to customize the graph
                </p>
              </div>
            )}
            <p className={styles["information-board--description"]}>
              Graph showing relationships of{" "}
              <b style={{ color: "var(--accent-color)" }}>
                top {filter.top_k}{" "}
              </b>
              results with most publications.
            </p>
            <ul className={styles["information-board--instructions"]}>
              <li>
                <DragOutlined /> Drag nodes to adjust view
              </li>
              <li>
                <CameraOutlined /> Take a snapshot
              </li>
              <li>
                <InfoCircleOutlined /> Click node/link for info
              </li>
            </ul>
          </div>
        </div>
      )}
      {selectedElement?.type == "node" && (
        <div className={styles["information-board"]}>
          <div className={styles["information-board--node"]}>
            <ImageCheck
              url={selectedElement.photo || selectedElement.logo}
              className={styles["information-board--avatar"]}
              altImg={getNodeAltLogo(selectedElement)}
              type={imageCheckType}
            />
            <div className={styles["information-board--brief"]}>
              <div className={styles["information-board--info"]}>
                <p className={styles["information-board--name"]}>
                  {selectedElement.name}
                </p>
                <p className={styles["information-board--institution"]}>
                  {selectedElement.institution_name ||
                    selectedElement.institution?.name}
                </p>
                <p
                  className={classNames(
                    styles["information-board--position"],
                    "dot-3"
                  )}
                >
                  {selectedElement.position}
                </p>
              </div>

              <div className={styles["information-board--statistics"]}>
                {statistics
                  .filter((e) => !!selectedElement[e.key])
                  .map((e, index) => (
                    <p
                      key={index}
                      className={styles["information-board--statistics--item"]}
                    >
                      <b>{numberRegex(selectedElement[e.key])}</b> {e.label}
                    </p>
                  ))}
              </div>

              <div className={styles["information-board--keywords"]}>
                {(selectedElement?.keywords ?? [])
                  .slice(0, 2)
                  .map((item, index) => (
                    <div
                      className={styles["information-board--keywords--item"]}
                      key={index}
                    >
                      {item?.value || item}
                    </div>
                  ))}
              </div>

              <Button
                variant="contained"
                size="small"
                className={styles["information-board--btn"]}
                href={`/explore/${graphType}/${selectedElement.id}`}
                startIcon={<InfoCircleFilled />}
              >
                More Info
              </Button>
            </div>
          </div>
        </div>
      )}
      {selectedElement?.type == "line" && (
        <div className={styles["information-board--line"]}>
          <div className={styles["information-board--line--node"]}>
            <ImageCheck
              url={
                selectedElement.fromNode.data.photo ||
                selectedElement.fromNode.data.logo
              }
              className={styles["information-board--line--node--avatar"]}
              altImg={getNodeAltLogo(selectedElement.fromNode.data)}
              type={imageCheckType}
            />
            <div className={styles["information-board--info"]}>
              <p
                className={styles["information-board--name"]}
                style={{ textAlign: "center" }}
              >
                {selectedElement.fromNode.data.name}
              </p>
              <p
                className={classNames(
                  styles["information-board--position"],
                  "dot-3"
                )}
                style={{ textAlign: "center" }}
              >
                {selectedElement.fromNode.data.institution_name ||
                  selectedElement.fromNode.data.position}
              </p>
            </div>
            <Button
              variant="contained"
              size="small"
              className={styles["information-board--btn"]}
              href={`/explore/${graphType}/${selectedElement.fromNode.id}`}
              startIcon={<InfoCircleFilled />}
            >
              More Info
            </Button>
          </div>
          <div className={styles["information-board--line--info"]}>
            <div className={styles["information-board--line--info__wrapper"]}>
              <div
                className={styles["information-board--line--info--left"]}
              ></div>
              <div className={styles["information-board--line--info--center"]}>
                {IconPack.publications}
                <p>{selectedElement.weight}</p>
              </div>
              <div
                className={styles["information-board--line--info--right"]}
              ></div>
            </div>
            <div
              className={styles["information-board--keywords"]}
              style={{ justifyContent: "center" }}
            >
              {(
                selectedElement.commonKeywords ||
                selectedElement.common_keywords
              )
                ?.slice(0, 5)
                .map((item, index) => {
                  return (
                    <div
                      className={styles["information-board--keywords--item"]}
                      key={index}
                    >
                      {item?.value || item}
                    </div>
                  );
                })}
            </div>
          </div>
          <div className={styles["information-board--line--node"]}>
            <ImageCheck
              url={
                selectedElement.toNode.data.photo ||
                selectedElement.toNode.data.logo
              }
              className={styles["information-board--line--node--avatar"]}
              type={imageCheckType}
              altImg={getNodeAltLogo(selectedElement.toNode.data)}
            />
            <div className={styles["information-board--info"]}>
              <p
                className={styles["information-board--name"]}
                style={{ textAlign: "center" }}
              >
                {selectedElement.toNode.data.name}
              </p>
              <p
                className={classNames(
                  styles["information-board--position"],
                  "dot-3"
                )}
                style={{ textAlign: "center" }}
              >
                {selectedElement.toNode.data.institution_name ||
                  selectedElement.toNode.data.position}
              </p>
            </div>
            <Button
              variant="contained"
              size="small"
              className={styles["information-board--btn"]}
              href={`/explore/${graphType}/${selectedElement.toNode.id}`}
              startIcon={<InfoCircleFilled />}
            >
              More Info
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default InformationBoard;
